import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../../axios";
import "./CustomizationAdvanced.scss";
import layouts from "../layouts/layouts";
import { useSelector } from "react-redux";
import { useAuth } from "../../../context/AuthContext";
import { FabricRenderer } from "../layouts/fabric";
import Settings from "./Settings";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Alert, CircularProgress } from "@mui/material";

const MIN_DOWNLOAD_SPINNER_MS = 650;
const waitForNextPaint = () =>
  new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve)),
  );
const waitForMinimumSpinner = (startedAt) =>
  new Promise((resolve) =>
    setTimeout(
      resolve,
      Math.max(0, MIN_DOWNLOAD_SPINNER_MS - (Date.now() - startedAt)),
    ),
  );

const CustomizationAdvanced = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const selectedLayoutType = params.get("selectedLayoutType");
  const selectedLayoutTypeType = params.get("selectedLayoutTypeType") || 0;
  const selectedTemplate = params.get("selectedTemplate");
  const navigate = useNavigate();
  const returnToGeneratingUrl = `/personForm?openGeneratingPanel=true&selectedLayoutType=${selectedLayoutType}&selectedLayoutTypeType=${selectedLayoutTypeType}&selectedTemplate=${encodeURIComponent(selectedTemplate || "")}`;
  const person = useSelector((state) => state.person.person);

  const { activeSubscription, can_edit_layout } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [buttonDisabledPDF, setButtonDisabledPDF] = useState(false);
  const [buttonDisabledJPG, setButtonDisabledJPG] = useState(false);
  const [isMirrorImage, setIsMirrorImage] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 800);
  const [canvasInstance, setCanvasInstance] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [pdfAlert, setPdfAlert] = useState(null);
  const [historyState, setHistoryState] = useState({
    canUndo: false,
    canRedo: false,
  });
  const isDownloading = buttonDisabledPDF || buttonDisabledJPG;
  const rendererRef = useRef(null);
  const historyRef = useRef([]);
  const redoRef = useRef([]);
  const isRestoringHistoryRef = useRef(false);
  const token = localStorage.getItem("token");

  const currentLayout =
    selectedLayoutType && selectedLayoutTypeType !== null
      ? layouts[selectedLayoutType]?.params[selectedLayoutTypeType]?.layout
      : null;

  const defaultTextColor = currentLayout?.theme?.defaultTextColor || "#000";

  useEffect(() => {
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 800);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!activeSubscription || !can_edit_layout) {
      navigate("/");
      window.scrollTo(0, 0);
    }
  }, [activeSubscription, can_edit_layout, navigate]);

  useEffect(() => {
    if (!pdfAlert) return undefined;

    const timeoutId = setTimeout(() => setPdfAlert(null), 4500);

    return () => clearTimeout(timeoutId);
  }, [pdfAlert]);

  const updateHistoryState = () => {
    setHistoryState({
      canUndo: historyRef.current.length > 1,
      canRedo: redoRef.current.length > 0,
    });
  };

  const serializeCanvas = () => {
    const canvas = rendererRef.current?.getCanvas?.();
    if (!canvas) return null;

    return JSON.stringify(canvas.toJSON(["elementId", "elementLabel"]));
  };

  const pushHistorySnapshot = () => {
    if (isRestoringHistoryRef.current) return;

    const snapshot = serializeCanvas();
    if (!snapshot) return;

    const lastSnapshot = historyRef.current[historyRef.current.length - 1];
    if (lastSnapshot === snapshot) return;

    historyRef.current = [...historyRef.current, snapshot].slice(-40);
    redoRef.current = [];
    updateHistoryState();
  };

  const restoreSnapshot = async (snapshot) => {
    const canvas = rendererRef.current?.getCanvas?.();
    if (!canvas || !snapshot) return;

    isRestoringHistoryRef.current = true;
    canvas.discardActiveObject();
    await canvas.loadFromJSON(JSON.parse(snapshot));
    canvas.renderAll();
    setSelectedObject(null);
    isRestoringHistoryRef.current = false;
  };

  const handleUndo = async () => {
    if (historyRef.current.length <= 1) return;

    const currentSnapshot = historyRef.current.pop();
    redoRef.current = [currentSnapshot, ...redoRef.current];
    await restoreSnapshot(historyRef.current[historyRef.current.length - 1]);
    updateHistoryState();
  };

  const handleRedo = async () => {
    const nextSnapshot = redoRef.current.shift();
    if (!nextSnapshot) return;

    historyRef.current = [...historyRef.current, nextSnapshot];
    await restoreSnapshot(nextSnapshot);
    updateHistoryState();
  };

  const handleResetLayout = () => {
    rendererRef.current?.rerender?.();
    historyRef.current = [];
    redoRef.current = [];
    setSelectedObject(null);
    window.requestAnimationFrame(() => {
      pushHistorySnapshot();
    });
  };

  async function handleDownloadPDF() {
    if (!rendererRef.current) return;
    const startedAt = Date.now();
    let nextAlert = null;

    setButtonDisabledPDF(true);
    setPdfAlert(null);

    try {
      await waitForNextPaint();
      const filename = `${person.surname}_${layouts[selectedLayoutType]?.name || "dokument"}.pdf`;
      await rendererRef.current.exportPDF(filename);
      nextAlert = { severity: "success", message: "PDF został pobrany." };
    } catch (error) {
      console.error("Błąd przy generowaniu PDF:", error);
      nextAlert = {
        severity: "error",
        message: "Nie udało się pobrać PDF. Spróbuj ponownie.",
      };
    } finally {
      await waitForMinimumSpinner(startedAt);
      setButtonDisabledPDF(false);
      setPdfAlert(nextAlert);
    }
  }

  async function handleDownloadJPG() {
    if (!rendererRef.current) return;
    const startedAt = Date.now();
    let nextAlert = null;

    setButtonDisabledJPG(true);
    setPdfAlert(null);

    try {
      await waitForNextPaint();
      const filename = `${person.surname}_${layouts[selectedLayoutType]?.name || "dokument"}.jpg`;
      await rendererRef.current.exportJPG(filename);
      nextAlert = { severity: "success", message: "JPG został pobrany." };
    } catch (error) {
      console.error("Błąd przy generowaniu JPG:", error);
      nextAlert = {
        severity: "error",
        message: "Nie udało się pobrać JPG. Spróbuj ponownie.",
      };
    } finally {
      await waitForMinimumSpinner(startedAt);
      setButtonDisabledJPG(false);
      setPdfAlert(nextAlert);
    }
  }

  const handleMirrorImageChange = (event) => {
    setIsMirrorImage(event.target.checked);
  };

  const handleElementSelect = (elementId, fabricObj) => {
    setSelectedObject(fabricObj || null);

    if (isMobile && fabricObj) {
      setIsSettingsOpen(true);
    }
  };

  const handleElementChange = () => {
    pushHistorySnapshot();
  };

  const templateUrl = selectedTemplate
    ? `${axios.defaults.imgURL}/${selectedTemplate}?token=${token}`
    : null;

  useEffect(() => {
    let frameId;

    const syncCanvasInstance = () => {
      const nextCanvas = rendererRef.current?.getCanvas?.() || null;
      setCanvasInstance((prevCanvas) =>
        prevCanvas === nextCanvas ? prevCanvas : nextCanvas,
      );

      if (!nextCanvas) {
        frameId = window.requestAnimationFrame(syncCanvasInstance);
      }
    };

    syncCanvasInstance();

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [currentLayout, templateUrl]);

  useEffect(() => {
    if (!canvasInstance) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      historyRef.current = [];
      redoRef.current = [];
      pushHistorySnapshot();
    });

    return () => window.cancelAnimationFrame(frameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasInstance, currentLayout, templateUrl]);

  return (
    <section className="customizationAdvanced_section">
      <div className="customizationAdvanced_topBar">
        <button
          className="btnDownload btnBackTop primary"
          onClick={() => navigate(returnToGeneratingUrl)}
          disabled={isDownloading}
        >
          <ArrowBackIcon fontSize="small" />
          Powrót
        </button>
      </div>

      <div className="customizationAdvanced_workspace">
        <div className="customizationAdvanced_previewArea">
          <div className="canvas-wrapper">
            {currentLayout && templateUrl && (
              <FabricRenderer
                key={`${selectedLayoutType}-${selectedLayoutTypeType}-${selectedTemplate}`}
                ref={rendererRef}
                layout={currentLayout}
                data={person}
                templateUrl={templateUrl}
                editable={true}
                isMirrorImage={isMirrorImage}
                onElementSelect={handleElementSelect}
                onElementChange={handleElementChange}
              />
            )}
          </div>
          <div className="customizationAdvanced_previewActions">
            <button
              className="btnDownload"
              onClick={handleDownloadPDF}
              disabled={
                isLoading ||
                !selectedLayoutType ||
                selectedTemplate === null ||
                isDownloading
              }
            >
              Pobierz PDF
            </button>
            <button
              className="btnDownload"
              onClick={handleDownloadJPG}
              disabled={
                isLoading ||
                !selectedLayoutType ||
                selectedTemplate === null ||
                isDownloading
              }
            >
              Pobierz JPG
            </button>
            <label className="generatingPanel_checkbox">
              <input
                type="checkbox"
                checked={isMirrorImage}
                onChange={handleMirrorImageChange}
              />
              Odbicie lustrzane
              <span className="checkmark"></span>
            </label>
          </div>
        </div>

        {currentLayout && (
          <>
            {isMobile && isSettingsOpen && (
              <div
                className="settingsOverlay"
                onClick={() => setIsSettingsOpen(false)}
              />
            )}
            <Settings
              canvas={canvasInstance}
              selectedObject={selectedObject}
              defaultTextColor={defaultTextColor}
              isMobile={isMobile}
              isOpen={!isMobile || isSettingsOpen}
              onClose={() => setIsSettingsOpen(false)}
              onHistoryCommit={pushHistorySnapshot}
              onUndo={handleUndo}
              onRedo={handleRedo}
              onResetLayout={handleResetLayout}
              canUndo={historyState.canUndo}
              canRedo={historyState.canRedo}
            />
            {isMobile && !isSettingsOpen && (
              <button
                className="settingsTab"
                onClick={() => setIsSettingsOpen(true)}
                aria-label="Otwórz ustawienia"
              >
                <SettingsIcon />
              </button>
            )}
          </>
        )}
      </div>

      {(isDownloading || pdfAlert) && (
        <div className="customizationAdvanced_status">
          {isDownloading && (
            <div className="downloadLoadingStatus">
              <CircularProgress size={22} />
              <span>
                {buttonDisabledPDF ? "Generowanie PDF..." : "Generowanie JPG..."}
              </span>
            </div>
          )}
          {pdfAlert && (
            <Alert
              severity={pdfAlert.severity}
              onClose={() => setPdfAlert(null)}
              className="downloadStatusAlert"
            >
              {pdfAlert.message}
            </Alert>
          )}
        </div>
      )}
    </section>
  );
};

export default CustomizationAdvanced;
